/**
 * Sign-in identifiers, secrets and hashing parameters shared by the server, the
 * seed and (from M4) the offline till. Pure: no Node or browser APIs.
 */
import { z } from 'zod';

/**
 * argon2id parameters for passwords, PINs and the offline PIN verifier
 * (02-architecture.md, "Security baseline"): the OWASP minimum for argon2id
 * (19 MiB, 2 passes, 1 lane). Stored hashes are PHC strings that carry their own
 * parameters, so raising these later only affects new hashes.
 */
export const ARGON2ID_PARAMS = Object.freeze({
  memoryCost: 19_456,
  timeCost: 2,
  parallelism: 1,
});

/** A phone number in E.164 (`+919876543210`). */
const E164 = /^\+[1-9]\d{7,14}$/;

/**
 * Normalise a phone number as staff type it, with India as the default country:
 * spaces, dashes, dots and brackets are ignored; `98765 43210`, `098765 43210`,
 * `91 98765 43210` and `+91 98765 43210` all become `+919876543210`. Other
 * countries need the `+` prefix. Returns null when it is not a phone number.
 */
export function normalizePhone(input: string): string | null {
  const raw = input.trim().replace(/[\s\-.()]/g, '');
  if (raw === '') return null;
  let candidate: string;
  if (raw.startsWith('+')) candidate = raw;
  else if (raw.startsWith('00')) candidate = `+${raw.slice(2)}`;
  else if (/^[6-9]\d{9}$/.test(raw)) candidate = `+91${raw}`;
  else if (/^0[6-9]\d{9}$/.test(raw)) candidate = `+91${raw.slice(1)}`;
  else if (/^91[6-9]\d{9}$/.test(raw)) candidate = `+${raw}`;
  else return null;
  return E164.test(candidate) ? candidate : null;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Lowercase, trimmed email, or null when it does not look like one. */
export function normalizeEmail(input: string): string | null {
  const v = input.trim().toLowerCase();
  return v.length <= 254 && EMAIL.test(v) ? v : null;
}

export type LoginIdentifier =
  | { readonly kind: 'phone'; readonly value: string }
  | { readonly kind: 'email'; readonly value: string };

/** A phone number or an email address, normalised; null when it is neither. */
export function parseLoginIdentifier(input: string): LoginIdentifier | null {
  if (input.includes('@')) {
    const email = normalizeEmail(input);
    return email === null ? null : { kind: 'email', value: email };
  }
  const phone = normalizePhone(input);
  return phone === null ? null : { kind: 'phone', value: phone };
}

/** A phone number in any common Indian or `+` international form; output is E.164. */
export const phoneSchema = z
  .string()
  .max(32)
  .refine((v) => normalizePhone(v) !== null, { message: 'Expected a phone number' })
  .overwrite((v) => normalizePhone(v) ?? v);

export const emailSchema = z
  .string()
  .max(254)
  .refine((v) => normalizeEmail(v) !== null, { message: 'Expected an email address' })
  .overwrite((v) => normalizeEmail(v) ?? v);

/** Phone number or email address as typed on the sign-in screen. */
export const loginIdentifierSchema = z
  .string()
  .min(3)
  .max(254)
  .refine((v) => parseLoginIdentifier(v) !== null, {
    message: 'Enter a phone number or an email address',
  });

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

/** A password being checked (any length the user may have). */
export const passwordSchema = z.string().min(1).max(PASSWORD_MAX_LENGTH);

/** A new password. */
export const newPasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, { message: `Use at least ${PASSWORD_MIN_LENGTH} characters` })
  .max(PASSWORD_MAX_LENGTH);

/** A staff PIN: 4 to 6 digits (02-architecture.md, "Security baseline"). */
export const pinSchema = z.string().regex(/^\d{4,6}$/, { message: 'Expected 4 to 6 digits' });

/**
 * PINs too easy to guess at a till: all one digit (`0000`) or a straight run up
 * or down (`1234`, `987654`).
 */
export function isWeakPin(pin: string): boolean {
  if (/^(\d)\1+$/.test(pin)) return true;
  const digits = [...pin].map(Number);
  const steps = digits.slice(1).map((d, i) => d - (digits[i] as number));
  return steps.every((s) => s === 1) || steps.every((s) => s === -1);
}

/** A new PIN: 4 to 6 digits, not trivially guessable. */
export const newPinSchema = pinSchema.refine((pin) => !isWeakPin(pin), {
  message: 'Choose a PIN that is not a repeated digit or a straight run like 1234',
});

/** A 6-digit one-time code (SMS/email OTP or TOTP). */
export const otpCodeSchema = z.string().regex(/^\d{6}$/, { message: 'Expected a 6-digit code' });

/** Random device secret a terminal generates on first run: at least 32 bytes, base64url. */
export const deviceSecretSchema = z
  .string()
  .min(43)
  .max(256)
  .regex(/^[A-Za-z0-9_-]+$/, { message: 'Expected a base64url string of at least 32 bytes' });

/** Alphabet of TOTP recovery codes: lowercase, no 0/1/i/l/o lookalikes. */
export const RECOVERY_CODE_ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz';
/** Recovery codes handed out when TOTP is turned on (each works once). */
export const RECOVERY_CODE_COUNT = 10;

/** A TOTP recovery code as typed: `abcde-fghjk` (case and the dash are ignored). */
export const recoveryCodeSchema = z
  .string()
  .regex(/^[A-Za-z0-9]{5}[- ]?[A-Za-z0-9]{5}$/, { message: 'Expected a code like abcde-fghjk' });

/** The canonical form of a recovery code (lowercase, no separator). */
export function normaliseRecoveryCode(code: string): string {
  return code.toLowerCase().replace(/[- ]/g, '');
}
